import type { Extensions } from "@tiptap/core";
import Blockquote from "@tiptap/extension-blockquote";
import Bold from "@tiptap/extension-bold";
import Code from "@tiptap/extension-code";
import { Details, DetailsContent, DetailsSummary } from "@tiptap/extension-details";
import Document from "@tiptap/extension-document";
import Emoji, { gitHubEmojis } from "@tiptap/extension-emoji";
import HardBreak from "@tiptap/extension-hard-break";
import Heading from "@tiptap/extension-heading";
import Highlight from "@tiptap/extension-highlight";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import Italic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import { ListKit } from "@tiptap/extension-list";
import MathExtension from "@tiptap/extension-mathematics";
import Paragraph from "@tiptap/extension-paragraph";
import Strike from "@tiptap/extension-strike";
import SubScript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import { TableOfContents } from "@tiptap/extension-table-of-contents";
import Text from "@tiptap/extension-text";
import TextAlign from "@tiptap/extension-text-align";
import { BackgroundColor, Color, TextStyle } from "@tiptap/extension-text-style";
import Typography from "@tiptap/extension-typography";
import Underline from "@tiptap/extension-underline";

import { CodeBlock, CodeBlockNodeMapping } from "./CustomExtensions/CodeBlock";
import { Embed, EmbedNodeMapping } from "./CustomExtensions/Embed";
import { Image, ImageNodeMapping } from "./CustomExtensions/Image";
import { LiveCodeBlock, LiveCodeBlockNodeMapping } from "./CustomExtensions/LiveCodeBlock";
import { Table } from "./CustomExtensions/Table";

export type RTEditorType = "form" | "document";

export const extensionMap: Record<RTEditorType, Extensions> = {
  form: [
    Document,
    Paragraph,
    Text,
    Heading.configure({
      levels: [1, 2, 3],
    }),
    HardBreak,
    Blockquote,

    // Marks
    Bold,
    Italic,
    Strike,
    Underline,

    // Extensions
    ListKit,

    Typography.configure({
      oneHalf: false,
      oneQuarter: false,
      threeQuarters: false,
      superscriptTwo: false,
      superscriptThree: false,
      servicemark: false,
      trademark: false,
      registeredTrademark: false,
    }),
  ],
  document: [
    // Nodes
    Document,
    Paragraph,
    Text,
    Heading.configure({
      levels: [1, 2, 3, 4],
    }),
    HardBreak,
    Blockquote,
    HorizontalRule,
    MathExtension,
    Details.configure({
      persist: true,
      HTMLAttributes: {
        class: "details",
      },
    }),
    DetailsSummary,
    DetailsContent,
    Emoji.configure({
      emojis: gitHubEmojis,
      enableEmoticons: true,
    }),
    CodeBlock,
    LiveCodeBlock,
    Table,
    Embed,
    Image,

    // Marks
    Bold,
    Italic,
    // https://github.com/ueberdosis/tiptap/issues/2571#issuecomment-1712057913
    Link.extend({
      inclusive: false,
    }).configure({
      openOnClick: false,
      autolink: true,
    }),
    Strike,
    Underline,
    TextStyle,
    Superscript,
    SubScript,
    Highlight.configure({
      HTMLAttributes: {
        class: "highlight",
      },
      multicolor: true,
    }),
    Code.configure({
      HTMLAttributes: {
        class: "inline-code",
      },
    }),

    // Extensions
    ListKit,
    Color,
    BackgroundColor,
    Typography.configure({
      oneHalf: false,
      oneQuarter: false,
      threeQuarters: false,
      superscriptTwo: false,
      superscriptThree: false,
      servicemark: false,
      trademark: false,
      registeredTrademark: false,
    }),
    TextAlign.configure({
      types: ["heading", "paragraph"],
    }),
    TableOfContents,
  ],
};

export const getExtensionList = (type: RTEditorType) => {
  return extensionMap[type];
};

export const nodeMapping = {
  ...ImageNodeMapping,
  ...EmbedNodeMapping,
  ...CodeBlockNodeMapping,
  ...LiveCodeBlockNodeMapping,
};
